import { FileMetadata } from "@domain/schemas/BI";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import axios, { CancelToken } from "axios";
import { inject, injectable } from "inversify";

@injectable()
export class LibraryRepository {
  private static readonly CHUNK_SIZE_BYTES = 8 * 1024 * 1024;

  private readonly baseUrlTemplate = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.libraries}`;
  private readonly uploadInitUrlTemplate = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.librariesUploadInit}`;
  private readonly uploadChunkUrlTemplate = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.librariesUploadChunk}`;
  private readonly uploadCompleteUrlTemplate = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.librariesUploadComplete}`;

  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getLibraries(projectId: string): Promise<FileMetadata[]> {
    const url = this.baseUrlTemplate.replace("{projectId}", () => encodeURIComponent(projectId));
    return this._requestHandlerService.handleRequestAsync<FileMetadata[]>(() => axios.get<FileMetadata[]>(url));
  }

  public async uploadLibrary(file: File, projectId: string, folder?: string, rootPath?: string | null, onProgress?: (progress: number) => void, cancelToken?: CancelToken): Promise<void> {
    const totalChunks = Math.max(1, Math.ceil(file.size / LibraryRepository.CHUNK_SIZE_BYTES));
    const uploadInitUrl = this.uploadInitUrlTemplate.replace("{projectId}", () => encodeURIComponent(projectId));
    const uploadChunkUrl = this.uploadChunkUrlTemplate.replace("{projectId}", () => encodeURIComponent(projectId));
    const uploadCompleteUrl = this.uploadCompleteUrlTemplate.replace("{projectId}", () => encodeURIComponent(projectId));

    const uploadId = await this._requestHandlerService.handleRequestAsync<string>(() =>
      axios.post<string>(
        uploadInitUrl,
        {
          fileName: file.name,
          folder,
          fileLength: file.size,
          rootPath: rootPath ?? undefined,
        },
        {
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
        },
      ),
    );

    const partTokens: string[] = [];
    let completedBytes = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * LibraryRepository.CHUNK_SIZE_BYTES;
      const end = Math.min(start + LibraryRepository.CHUNK_SIZE_BYTES, file.size);
      const chunk = file.slice(start, end);
      const partNumber = chunkIndex + 1;

      const partToken = await this._requestHandlerService.handleRequestAsync<string>(() =>
        axios.post<string>(uploadChunkUrl, chunk, {
          params: {
            uploadId,
            partNumber,
            fileName: file.name,
            folder,
            rootPath: rootPath ?? undefined,
          },
          headers: {
            "Content-Type": "application/octet-stream",
          },
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
          onUploadProgress: (progressEvent) => {
            if (!onProgress) {
              return;
            }

            const loaded = Math.min(completedBytes + (progressEvent.loaded ?? 0), file.size);
            const percentCompleted = Math.min(99, Math.round((loaded * 100) / (file.size || 1)));
            onProgress(percentCompleted);
          },
        }),
      );

      partTokens.push(partToken);
      completedBytes = end;

      if (!onProgress) continue;
      const percentCompleted = chunkIndex === totalChunks - 1 ? 99 : Math.min(99, Math.round((completedBytes * 100) / (file.size || 1)));
      onProgress(percentCompleted);
    }

    await this._requestHandlerService.handleRequestAsync<void>(() =>
      axios.post<void>(
        uploadCompleteUrl,
        {
          uploadId,
          partTokens,
          fileName: file.name,
          folder,
          md5Hash: null,
          rootPath: rootPath ?? undefined,
        },
        {
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
        },
      ),
    );

    if (onProgress) {
      onProgress(100);
    }
  }
}
